import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Select,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";

import useFetchObjects from "../../api/useFetchObjects";
import { printSalesPaymentEnglish } from "../utils/printSalesPaymentEnglish";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { exportSalePaymentsToPDF } from "../../utils/exportSalePayments";

const firstCurrencyValue = (obj) => {
  if (obj && typeof obj === "object") {
    const firstEntry = Object.values(obj)[0];
    if (firstEntry && typeof firstEntry === "object")
      return firstEntry.source ?? "-";
    return firstEntry ?? "-";
  }
  return obj ?? "-";
};

export default function SalePaymentTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSale, setSelectedSale] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const open = Boolean(anchorEl);

  const queryString = useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    params.push(`page=${page}`);
    if (rowsPerPage !== -1) {
      params.push(`page_size=${rowsPerPage}`);
    }
    return params.join("&");
  }, [startDate, endDate, selectedCustomer, page, rowsPerPage]);

  // Use the correct sale_payments endpoint from Django URLs
  const {
    data: salePaymentsData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["sale-payments-correct", queryString, selectedCustomer],
    `shop/sale_payments/?${queryString}`,
    token
  );

  // Fetch customers for filter dropdown
  const { data: customersData, isLoading: customersLoading, isError: customersError } = useFetchObjects(
    ["customers-for-filter"],
    "shop/customers/?page_size=1000", // Fetch more customers for the filter
    token
  );

  const customers = customersData?.results || [];
  const payments = salePaymentsData?.results || [];
  const totalCount = salePaymentsData?.count || 0;

  // Filter by sale reference if selected
  const filteredPayments = useMemo(() => {
    let filtered = payments;
    if (selectedSale) {
      filtered = filtered.filter(payment => 
        `Sale ${payment.sale_details?.id || payment.sale}`.toLowerCase().includes(selectedSale.toLowerCase())
      );
    }
    return filtered;
  }, [payments, selectedSale]);

  const finalCount = filteredPayments.length;
  const displayPayments = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredPayments; // Show all when "All" is selected
    }
    const startIndex = (page - 1) * rowsPerPage;
    return filteredPayments.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredPayments, page, rowsPerPage]);

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    return displayPayments.reduce((acc, payment) => {
      const amount = parseFloat(payment.amount) || 0;
      const totalRemained = parseFloat(firstCurrencyValue(payment.sale_details?.total_remained_amount)) || 0;
      
      return {
        totalAmount: acc.totalAmount + amount,
        totalRemained: acc.totalRemained + totalRemained
      };
    }, { totalAmount: 0, totalRemained: 0 });
  }, [displayPayments]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `sale_payments_${today}`;
    if (type === "pdf") exportSalePaymentsToPDF(payments, `${base}.pdf`);
    handleClose();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Filters */}
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <InputLabel id="customer-select-label">
                    {t("ReportIndex.customer_filter", "Customer")}
                  </InputLabel>
                  <Select
                    labelId="customer-select-label"
                    label={t("ReportIndex.customer_filter", "Customer")}
                    value={selectedCustomer}
                    onChange={(e) => {
                      setSelectedCustomer(e.target.value);
                      setPage(1); // Reset to first page when filter changes
                    }}
                    displayEmpty
                    disabled={customersLoading}
                  >
                    <MenuItem value="">
                      <em>{t("ReportIndex.all_customers", "All Customers")}</em>
                    </MenuItem>
                    {customersLoading ? (
                      <MenuItem disabled>
                        <em>Loading customers...</em>
                      </MenuItem>
                    ) : customersError ? (
                      <MenuItem disabled>
                        <em>Error loading customers</em>
                      </MenuItem>
                    ) : customers.length > 0 ? (
                      customers.map((customer) => (
                        <MenuItem key={customer.id} value={customer.id}>
                          {customer.name || `Customer ${customer.id}`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <em>No customers available</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="sale-select-label">
                    {t("ReportIndex.sale_filter", "Sale")}
                  </InputLabel>
                  <Select
                    labelId="sale-select-label"
                    label={t("ReportIndex.sale_filter", "Sale")}
                    value={selectedSale}
                    onChange={(e) => {
                      setSelectedSale(e.target.value);
                      setPage(1); // Reset to first page when filter changes
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{t("ReportIndex.all_sales", "All Sales")}</em>
                    </MenuItem>
                    {payments.length > 0 ? (
                      [...new Set(payments.map(payment => `Sale ${payment.sale_details?.id || payment.sale}`))]
                        .filter(saleRef => saleRef !== "Sale undefined" && saleRef !== "Sale ")
                        .map((saleRef, index) => (
                          <MenuItem key={index} value={saleRef}>
                            {saleRef}
                          </MenuItem>
                        ))
                    ) : (
                      <MenuItem disabled>
                        <em>No sales available</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedCustomer("");
                    setSelectedSale("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      {/* Sale Payment Table */}
      <TableContainer component={Paper}>
        <Table aria-label="sale payment table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="h4">
                  {t("reports.salePayment_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("reports.salePayment_table_id")}</TableCell>
              <TableCell>{t("reports.salePayment_table_customer")}</TableCell>
              <TableCell>{t("reports.salePayment_table_reference")}</TableCell>
              <TableCell>{t("reports.salePayment_table_amount")}</TableCell>
              <TableCell>{t("reports.salePayment_table_currency")}</TableCell>
              <TableCell>{t("reports.salePayment_table_paymentDate")}</TableCell>
              <TableCell>{t("reports.salePayment_table_totalRemained")}</TableCell>
              <TableCell>{t("reports.action", "Action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayPayments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{payment.id}</TableCell>
                <TableCell>{payment.sale_details?.customer || "-"}</TableCell>
                <TableCell>Sale {payment.sale_details?.id || payment.sale || "-"}</TableCell>
                <TableCell>{payment.amount || "0.00"}</TableCell>
                <TableCell>{payment.currency_details?.title || "AF"}</TableCell>
                <TableCell>
                  {payment.payment_date
                    ? new Date(payment.payment_date).toLocaleString()
                    : payment.created_at
                    ? new Date(payment.created_at).toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell>
                  {firstCurrencyValue(payment.sale_details?.total_remained_amount) || "0"} {payment.currency_details?.title || "AF"}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={() =>
                      printSalesPaymentEnglish({
                        invoiceNumber: payment.id,
                        date: payment.payment_date || payment.created_at,
                        customerName: payment.sale_details?.customer || "-",
                        requestedAmount: payment.amount ?? "0.00",
                        currencySymbol: payment.currency_details?.title || "AF",
                      })
                    }
                  >
                    {t("reports.print", "Print")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalAmount.toFixed(2)}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRemained.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center">
          <Typography>{t("reports.rowsPerPage")}</Typography>
          <FormControl sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } } }}>
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={-1}>{t("reports.all")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== -1 && (
          <Pagination
            count={Math.ceil(finalCount / rowsPerPage)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        )}
      </Grid>
    </>
  );
}